/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import * as React from 'react';
import { useHistory } from 'react-router-dom';

import {
    currentModeVar,
    Mode,
    templateInstanceCategoryVar,
    templateInstanceDescriptionVar,
    templateInstanceFormEntriesVar,
    templateInstanceFormIdVar,
    templateInstanceIconBackgroundColorVar,
    templateInstanceIconNameVar,
    templateInstanceIdVar,
    templateInstanceIsApproverCommentsRequiredVar,
    templateInstanceIsFileAttachmentRequiredVar,
    templateInstanceSpecificPeopleVar,
    templateInstanceStatusVar,
    templateInstanceTitleVar,
    updateTemplateInstanceInputCopyVar,
} from '../../../apollo/reactiveVariables/TemplateInstanceVariables';
import {
    currentScopeInCreationVar,
    currentScopeInListVar,
    currentTeamInCreationVar,
    currentTemplateVar,
    modalDataVar,
    TemplateScope,
} from '../../../apollo/reactiveVariables/TemplateManagementVariables';
import { Paths } from '../../../common/enums/Paths';
import { mapFormEntriesToQuestionInputs, mapFormEntriesToQuestions } from '../../../forms';
import { LogCategory, PanelRegion, PanelType, ScenarioName, StatusName, StepName, SubWorkLoad } from '../../../logger';
import {
    ApprovalTemplateInstance,
    ApprovalTemplateInstanceScopeType,
    CreateApprovalTemplateInstanceBaseInput,
    CreateApprovalTemplateInstanceClient,
    DynamicRouteItemInput,
    DynamicRouteType,
    RouteTypes,
    StepRouteSettingInput,
    UpdateApprovalTemplateInstanceClient,
    UpdateApprovalTemplateInstanceInput,
    UpdateSelfOwnedApprovalTemplateInstanceInput,
    VisibilityPrincipal,
    VisibilityPrincipalInput,
} from '../../../service/gen/templates';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { getPatchObj, getTemplateScenarioName, SubstrateServiceErrorCode } from '../../../utilities/TemplateUtils';
import { isSelfOwnedFormEnabled } from '../../../utilities/Utils';
import { ErrorPageProps } from '../../containers/ErrorPage';
import {
    useTemplateInstanceWorkflowSchemaInput,
    useTemplateInstanceWorkflowSchemaPreview,
} from '../CreateTemplateInstanceWizard/steps/WorkflowDesign/WorkflowSettings.container';
import { DeployTemplateComponent, DeployTemplateComponentProps } from './DeployTemplate.component';

export const DeployTemplateContainer: React.FC = (): JSX.Element => {
    const host = getAppHost();
    const loc = host.getLocale();
    const logger = host.getLogger();
    const id = useReactiveVar(templateInstanceIdVar);
    const title = useReactiveVar(templateInstanceTitleVar)?.trim();
    const description = useReactiveVar(templateInstanceDescriptionVar)?.trim();
    const iconName = useReactiveVar(templateInstanceIconNameVar);
    const iconBackgroundColor = useReactiveVar(templateInstanceIconBackgroundColorVar);
    const specificPeople = useReactiveVar(templateInstanceSpecificPeopleVar);
    const category = useReactiveVar(templateInstanceCategoryVar);
    const status = useReactiveVar(templateInstanceStatusVar);
    const formId = useReactiveVar(templateInstanceFormIdVar);
    const formEntries = useReactiveVar(templateInstanceFormEntriesVar);

    const isFileAttachmentRequired = useReactiveVar(templateInstanceIsFileAttachmentRequiredVar);
    const isApproverCommentsRequired = useReactiveVar(templateInstanceIsApproverCommentsRequiredVar);

    const template = useReactiveVar(currentTemplateVar);

    const currentMode = useReactiveVar(currentModeVar);
    const currentScope = useReactiveVar(currentScopeInCreationVar);
    const currentTeam = useReactiveVar(currentTeamInCreationVar);

    const updateTemplateInstanceInputCopy = useReactiveVar(updateTemplateInstanceInputCopyVar);
    const templateInstanceWorkflowSchemaInput = useTemplateInstanceWorkflowSchemaInput();
    const templateInstanceWorkflowSchemaPreview = useTemplateInstanceWorkflowSchemaPreview();

    const history = useHistory();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [errorProps, setErrorProps] = React.useState<ErrorPageProps>(null);

    const useTeamTagsAsApprover = !!templateInstanceWorkflowSchemaInput.DynamicRoute?.Items?.filter(
        (item: DynamicRouteItemInput): boolean => item.Type === DynamicRouteType.TeamWorkTag
    )?.length;

    const onNext = async (): Promise<void> => {
        setIsLoading(true);
        const startTime = performance.now();
        const currentScenarioName = getTemplateScenarioName(currentMode);
        let requestPromise: Promise<any>;
        if (currentMode === Mode.Create) {
            const createInstanceInput: CreateApprovalTemplateInstanceBaseInput = {
                Category: category,
                Description: description,
                AccentColor: iconBackgroundColor,
                IconName: iconName,
                Title: title,
                WorkflowSchema: templateInstanceWorkflowSchemaInput,
                TeamId: currentTeam?.id,
                IsEnabled: status,
                IsVisibleToAll: currentScope === TemplateScope.TenantAll,
                VisibleTo: currentScope === TemplateScope.TenantSpecificPeople ? specificPeople : [],
                Scope:
                    currentScope === TemplateScope.Team
                        ? ApprovalTemplateInstanceScopeType.Team
                        : ApprovalTemplateInstanceScopeType.Organization,
                IsFileAttachmentRequired: isFileAttachmentRequired,
                IsApproverCommentsRequired: isApproverCommentsRequired,
            };

            const client = new CreateApprovalTemplateInstanceClient();
            if (isSelfOwnedFormEnabled()) {
                requestPromise = client.createSelfOwnedApprovalTemplateInstance({
                    ...createInstanceInput,
                    Questions: mapFormEntriesToQuestionInputs(formEntries),
                });
            } else {
                requestPromise = client.createApprovalTemplateInstance({
                    ...createInstanceInput,
                    FormId: formId,
                });
            }
        } else {
            const updateInstanceInput:
                | UpdateApprovalTemplateInstanceInput
                | UpdateSelfOwnedApprovalTemplateInstanceInput = {
                Category: category,
                Description: description,
                AccentColor: iconBackgroundColor,
                IconName: iconName,
                Title: title,
                WorkflowSchema: templateInstanceWorkflowSchemaInput,
                Id: id,
                IsEnabled: status,
                VisibleTo:
                    currentScope === TemplateScope.TenantSpecificPeople
                        ? specificPeople?.map(
                              (principal: VisibilityPrincipal): VisibilityPrincipalInput => ({
                                  PrincipalId: principal.PrincipalId,
                                  PrincipalType: principal.PrincipalType,
                              })
                          )
                        : [],
                IsVisibleToAll: currentScope === TemplateScope.TenantAll,
                IsFileAttachmentRequired: isFileAttachmentRequired,
                IsApproverCommentsRequired: isApproverCommentsRequired,
                Questions: mapFormEntriesToQuestions(formEntries),
            };

            const patchInput = getPatchObj(updateTemplateInstanceInputCopy, updateInstanceInput);
            patchInput.WorkflowSchema = updateInstanceInput.WorkflowSchema;
            patchInput.Id = updateTemplateInstanceInputCopy.Id;

            const client = new UpdateApprovalTemplateInstanceClient();
            if (isSelfOwnedFormEnabled()) {
                requestPromise = client.updateSelfOwnedApprovalTemplateInstance(patchInput);
            } else {
                requestPromise = client.updateApprovalTemplateInstance(patchInput);
            }
        }
        const isSequential = templateInstanceWorkflowSchemaInput.RouteTypes === RouteTypes.StepRoute;
        const telemetryData = {
            specificPeopleSize: specificPeople?.length,
            approversSize: templateInstanceWorkflowSchemaInput.Approvers?.length,
            allMustApprove: templateInstanceWorkflowSchemaInput.AllMustRespond ? 'True' : 'False',
            numberOfSteps: isSequential ? templateInstanceWorkflowSchemaInput.StepRoutes?.length : 0,
            steps: isSequential
                ? templateInstanceWorkflowSchemaInput.StepRoutes?.map(
                      (step: StepRouteSettingInput): RouteTypes => step.RouteTypes
                  ).join()
                : '',
            titleSize: title.length,
            descriptionSize: description?.length,
            customResponseSize: templateInstanceWorkflowSchemaInput.CustomResponseOptions?.length,
            status,
            category,
            templateId: template?.id,
            scope: currentScope,
            teamId: currentTeam?.id,
            isFileAttachmentRequired,
            isApproverCommentsRequired,
            routeType: templateInstanceWorkflowSchemaInput.RouteTypes,
            staticRouteApproverAmount: templateInstanceWorkflowSchemaInput.StaticRoute?.Approvers?.length,
            staticRouteAllMustRespond: templateInstanceWorkflowSchemaInput.StaticRoute?.AllMustRespond,
            fallbackPolicyType: templateInstanceWorkflowSchemaInput.FallbackPolicy?.Type,
            fallbackPolicyApproversAmount:
                templateInstanceWorkflowSchemaInput.FallbackPolicy?.FallbackApprovers?.length,
            fallbackPolicyAllMustRespond:
                templateInstanceWorkflowSchemaInput.FallbackPolicy?.FallbackApproversAllMustRespond,
            formId,
            useTeamTagsAsApprover,
        };

        logger.markScenario(currentScenarioName, StepName.PublishButtonPressed, StatusName.NA, {
            data: telemetryData,
        });

        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            currentScenarioName,
            StepName.PublishButtonPressed,
            'click',
            'submit',
            'publishTemplateInstance',
            'publishTemplateInstanceButton',
            PanelRegion.Modal,
            PanelType.PreviewTemplateInstance,
            telemetryData
        );

        requestPromise
            .then((response: ApprovalTemplateInstance): void => {
                logger.logTraceInfo(LogCategory.TemplateInstanceWizard, `${currentScenarioName} is complete`);
                logger.findScenario(currentScenarioName).stop({
                    stepElapsed: performance.now() - startTime,
                    data: {
                        ...telemetryData,
                        templateInstanceId: response.Id,
                    },
                });
                logger.updateCommonProperty({ templateInstanceId: '' });
                logger.markScenarioComplete(currentScenarioName);
                logger.markScenario(
                    ScenarioName.TemplateManagement,
                    StepName.ExitTemplateInstanceWizard,
                    StatusName.NA
                );

                currentScopeInListVar({
                    scope: currentScope,
                    team: currentTeam && { id: currentTeam.id, name: currentTeam.name },
                });

                setErrorProps(null);
                modalDataVar({ isOpen: false });
                history.push({
                    pathname: Paths.BasePath + Paths.TemplateManagement,
                });
            })
            .catch((error: any): void => {
                logger.logTraceHttpException(
                    currentMode === Mode.Create ? 'createApprovalTemplateInstance' : 'updateApprovalTemplateInstance',
                    error?.message,
                    error?.status
                );

                logger.markScenario(currentScenarioName, currentScenarioName, StatusName.Fail, {
                    stepElapsed: performance.now() - startTime,
                    data: {
                        ...telemetryData,
                    },
                });

                const errorMessage = JSON.parse(error?.response)?.Properties?.message as string;
                const innerMessage = JSON.parse(error?.response)?.Properties?.innerMessage as string;

                // TODO: Refactor the following code after backend data structure is ready.
                if (
                    errorMessage === SubstrateServiceErrorCode.SDSTenantShardProvisioningInProgress ||
                    innerMessage?.includes(SubstrateServiceErrorCode.ErrorInvalidOrgPartition)
                ) {
                    setErrorProps({ onAction: onNext, message: loc.strings.onboard_substrate_wait_tip });
                } else {
                    setErrorProps({ onAction: onNext });
                }
            })
            .finally((): void => {
                setIsLoading(false);
            });
    };

    const onCancel = (): void => {
        modalDataVar({ isOpen: false });
    };

    const previewProps: DeployTemplateComponentProps = {
        onNext,
        onCancel,
        description,
        title,
        iconName,
        iconBackgroundColor,
        isLoading,
        mode: currentMode,
        errorProps,
        templatePreviewProps: {
            formId,
            formEntries,
            workflowSchema: templateInstanceWorkflowSchemaPreview,
            showFileUpload: isFileAttachmentRequired,
        },
    };

    return <DeployTemplateComponent {...previewProps} />;
};
